import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { SESSION_TOKEN_STORAGE_KEY } from '@scan2eat/shared';
import { Icon } from '../components/Icon.js';
import { Logo } from '../components/Logo.js';
import {
  cameraAvailability,
  classifyCameraError,
  createQrDecoder,
  extractQrToken,
  openRearCamera,
  type CameraFailure,
} from '../lib/qr-scanner.js';
import { readStoredValue } from '../lib/storage.js';
import { labels } from '../labels.js';
import styles from './ScanLandingPage.module.css';

/** Pause entre deux lectures d'image : assez vif pour l'oeil, sobre pour la batterie. */
const SCAN_INTERVAL_MS = 250;

type CameraState = 'starting' | 'scanning' | CameraFailure;

export function ScanLandingPage() {
  const navigate = useNavigate();
  // Par la constante partagee et son repli : depuis le renommage (ADR 0020),
  // la cle en dur 'emenu.sessionToken' ne voyait plus aucun nouveau convive.
  const token = readStoredValue(SESSION_TOKEN_STORAGE_KEY);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [manual, setManual] = useState('');
  const [manualError, setManualError] = useState(false);
  const [camera, setCamera] = useState<CameraState>('starting');
  // Incremente par « Reessayer » : relance l'effet de la camera, depuis un
  // geste de l'utilisateur, ce que Safari exige pour reposer la question.
  const [attempt, setAttempt] = useState(0);

  // Si une session existe déjà, on va directement au menu.
  useEffect(() => {
    if (token) navigate('/menu', { replace: true });
  }, [token, navigate]);

  // Camera et lecture du QR code. BarcodeDetector quand le navigateur le
  // fournit, jsqr sinon : Safari ne le fournit pas (ADR 0031).
  useEffect(() => {
    if (token) return;
    const unavailable = cameraAvailability();
    if (unavailable) {
      setCamera(unavailable);
      return;
    }

    const video = videoRef.current;
    let cancelled = false;
    let stream: MediaStream | null = null;
    let timer: number | undefined;
    setCamera('starting');

    const start = async () => {
      try {
        const [opened, decode] = await Promise.all([openRearCamera(), createQrDecoder()]);
        stream = opened;
        if (cancelled || !video) {
          opened.getTracks().forEach((t) => t.stop());
          return;
        }
        video.srcObject = opened;
        await video.play().catch(() => undefined);
        setCamera('scanning');

        const tick = async () => {
          if (cancelled) return;
          try {
            const raw = video.readyState >= 2 ? await decode(video) : null;
            const found = raw ? extractQrToken(raw) : null;
            if (found && !cancelled) {
              navigate(`/t/${found}`, { replace: true });
              return;
            }
          } catch {
            /* image non décodable, on réessaie */
          }
          timer = window.setTimeout(() => void tick(), SCAN_INTERVAL_MS);
        };
        void tick();
      } catch (error) {
        if (!cancelled) setCamera(classifyCameraError(error));
      }
    };

    void start();
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
      stream?.getTracks().forEach((t) => t.stop());
      if (video) video.srcObject = null;
    };
  }, [token, navigate, attempt]);

  const retry = useCallback(() => setAttempt((n) => n + 1), []);

  const submitManual = (e: React.FormEvent) => {
    e.preventDefault();
    const found = extractQrToken(manual);
    if (!found) {
      setManualError(true);
      return;
    }
    navigate(`/t/${found}`, { replace: true });
  };

  if (token) return null;

  const scanning = camera === 'scanning';
  const failed = camera !== 'scanning' && camera !== 'starting';

  return (
    <main className={styles.screen}>
      <Link to="/" className={styles.back} aria-label={labels.scan.back}>
        <Icon name="arrow-left" size={22} />
      </Link>

      <div className={styles.body}>
        <div className={styles.viewfinder}>
          {/* Toujours affichee : Safari ne decode pas les images d'une video
              masquee par display: none, et jsqr lirait du noir. */}
          <video
            ref={videoRef}
            muted
            autoPlay
            playsInline
            className={`${styles.camera} ${scanning ? '' : styles.cameraIdle}`}
          />
          {!scanning && (
            <div className={styles.placeholder}>
              <p>{failed ? labels.scan.camera[camera] : labels.scan.starting}</p>
              {failed && camera !== 'insecure' && camera !== 'unsupported' && (
                <button type="button" className={`btn-secondary ${styles.retry}`} onClick={retry}>
                  {labels.scan.retry}
                </button>
              )}
            </div>
          )}
          <span className={`${styles.corner} ${styles.cornerTl}`} aria-hidden="true" />
          <span className={`${styles.corner} ${styles.cornerTr}`} aria-hidden="true" />
          <span className={`${styles.corner} ${styles.cornerBl}`} aria-hidden="true" />
          <span className={`${styles.corner} ${styles.cornerBr}`} aria-hidden="true" />
          {scanning && <span className={styles.laser} aria-hidden="true" />}
        </div>

        <h1 className={styles.title}>{labels.scan.title}</h1>
        <p className={styles.intro}>{labels.scan.intro}</p>
        <p className={styles.status} role="status" aria-live="polite">
          {scanning ? labels.scan.scanning : failed ? labels.scan.nativeCameraTip : ''}
        </p>

        <form onSubmit={submitManual} className={styles.form}>
          <label htmlFor="qr-token" className={styles.label}>
            {labels.scan.manualLabel}
          </label>
          <div className={styles.row}>
            <input
              id="qr-token"
              name="qr-token"
              type="text"
              inputMode="url"
              autoComplete="off"
              autoCapitalize="off"
              spellCheck={false}
              value={manual}
              onChange={(e) => { setManual(e.target.value); setManualError(false); }}
              placeholder={labels.scan.manualPlaceholder}
              className={`input ${styles.input}`}
            />
            <button type="submit" className="btn-secondary">{labels.scan.access}</button>
          </div>
          {manualError && <p role="alert" className={styles.error}>{labels.scan.invalid}</p>}
        </form>

        <p className={styles.hint}>{labels.scan.hint}</p>
      </div>

      <footer className={styles.footer}>
        <Logo size={28} withWordmark tone="dark" />
      </footer>
    </main>
  );
}
